/**
 * Utility to handle browser-level file downloads and previews from Blobs.
 */
export const fileUtils = {
  /**
   * Opens a Blob in a new browser tab.
   * Handles the URL lifecycle (creation and revocation).
   *
   * @param blob The blob content to display
   * @param revokeDelay Ms to wait before revoking the URL (default 1000)
   */
  openInNewTab: (blob: Blob, revokeDelay = 1000) => {
    const url = window.URL.createObjectURL(blob);
    const win = window.open(url, '_blank');

    if (win) {
      win.focus();
    }

    // Revoke the URL after a delay to allow the browser to process it
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, revokeDelay);
  },

  /**
   * Triggers a browser download for a Blob.
   *
   * @param blob The blob content to download
   * @param fileName The name of the file to be saved
   */
  downloadFile: (blob: Blob, fileName: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();

    // Cleanup
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);
  }
};

/**
 * Response handler for file download endpoints
 * Extracts filename from Content-Disposition header and downloads the file
 * @param response - The fetch response
 * @param defaultFilename - Default filename if Content-Disposition header is not present
 */
export const handleFileDownloadResponse = async (
  response: Response,
  defaultFilename: string = 'download'
): Promise<void> => {
  const blob = await response.blob();
  const contentDisposition = response.headers.get('Content-Disposition');
  const filename = extractFilenameFromContentDisposition(contentDisposition) || defaultFilename;
  fileUtils.downloadFile(blob, filename);
};

/**
 * Extracts filename from Content-Disposition header
 * @param contentDisposition - The Content-Disposition header value
 * @returns The extracted filename or null
 */
export const extractFilenameFromContentDisposition = (contentDisposition: string | null): string | null => {
  if (!contentDisposition) return null;

  const match = contentDisposition.match(/filename="(.+)"/);
  return match ? match[1] : null;
};

/**
 * Creates FormData for multipart request with JSON data and files
 * @param data - JSON data to send
 * @param files - Optional array of files to attach
 * @param metadata - Optional metadata to append to FormData
 */
export const createMultipartFormData = <T extends Record<string, unknown>>(
  data: T,
  files?: File[],
  metadata?: Record<string, string>
): FormData => {
  const formData = new FormData();

  // Add JSON data as blob with explicit content type
  const jsonBlob = new Blob([JSON.stringify(data)], { type: 'application/json' });
  formData.append('data', jsonBlob);

  // Add files if provided
  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files', file);
    });
  }

  // Add metadata fields
  if (metadata) {
    Object.entries(metadata).forEach(([key, value]) => {
      if (value) {
        formData.append(key, value);
      }
    });
  }

  return formData;
};

/**
 * Formats file size in human-readable format
 * @param bytes - File size in bytes
 * @returns Formatted file size string
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};
